// The write queue.
//
// Logging a set never touches the network. It writes the set and its queue
// entry to IndexedDB in one transaction, and the queue drains whenever a
// connection happens to exist - from the page, or from the service worker's
// background sync event after the app has been closed.
//
// Replay safety comes from the row ids: every record carries a uuid the device
// minted, and the Worker upserts on it. Sending the same set twice writes the
// same row twice, which is to say once.

import { api, ApiError, OfflineError } from './api';
import { getAll, STORE, tx, wrap, type OutboxRecord } from './idb';
import type { LocalSet, LocalSession } from './types';

const BATCH = 50;

export function newId(): string {
  // randomUUID needs a secure context; localhost and https both qualify, but
  // fall back rather than throw if the app is opened over plain http on the LAN.
  const native = (crypto as Crypto & { randomUUID?: () => string }).randomUUID;
  if (typeof native === 'function') return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

function entry(op: OutboxRecord['op'], payload: Record<string, unknown>): OutboxRecord {
  return { id: newId(), op, payload, at: new Date().toISOString(), tries: 0 };
}

// --- writes -------------------------------------------------------------

/** Persist a session locally and queue it. One transaction: both or neither. */
export async function commitSession(session: LocalSession): Promise<void> {
  await tx([STORE.sessions, STORE.outbox], 'readwrite', (t) => {
    t.objectStore(STORE.sessions).put(session);
    t.objectStore(STORE.outbox).add(
      entry('session.upsert', {
        id: session.id,
        plan_day_id: session.plan_day_id,
        started_at: session.started_at,
        mode: session.mode,
        energy_before: session.energy_before,
      }),
    );
  });
}

/**
 * Persist a logged set and queue it.
 *
 * This is the one call in the app that must not fail and must not wait. It
 * writes to the device and returns; the network is somebody else's problem.
 */
export async function commitSet(set: LocalSet): Promise<void> {
  await tx([STORE.sets, STORE.outbox], 'readwrite', (t) => {
    t.objectStore(STORE.sets).put(set);
    t.objectStore(STORE.outbox).add(
      entry('set.upsert', {
        id: set.id,
        session_id: set.session_id,
        exercise_id: set.exercise_id,
        plan_item_id: set.plan_item_id,
        set_index: set.set_index,
        order_index: set.order_index,
        weight_kg: set.weight_kg,
        reps: set.reps,
        time_seconds: set.time_seconds,
        effort: set.effort,
        completed: set.completed,
        skip_reason: set.skip_reason,
        performed_at: set.performed_at,
      }),
    );
  });
}

export async function commitFinish(session: LocalSession): Promise<void> {
  await tx([STORE.sessions, STORE.outbox], 'readwrite', (t) => {
    t.objectStore(STORE.sessions).put(session);
    t.objectStore(STORE.outbox).add(
      entry('session.finish', {
        id: session.id,
        finished_at: session.finished_at,
        duration_seconds: session.duration_seconds,
      }),
    );
  });
}


/**
 * Save the local-only part of a session (the cursor into the set queue).
 *
 * No outbox entry: the server does not care which set he is on, and queuing
 * one per tap would fill the queue with noise.
 */
export async function saveLocalSession(session: LocalSession): Promise<void> {
  await tx([STORE.sessions], 'readwrite', (t) => {
    t.objectStore(STORE.sessions).put({ ...session });
  });
}

// --- draining -----------------------------------------------------------

export async function depth(): Promise<number> {
  return tx([STORE.outbox], 'readonly', (t) => wrap(t.objectStore(STORE.outbox).count()));
}

export async function deadLetters(): Promise<OutboxRecord[]> {
  return getAll<OutboxRecord>(STORE.dead);
}

async function head(limit: number): Promise<OutboxRecord[]> {
  return tx([STORE.outbox], 'readonly', async (t) => {
    const rows: OutboxRecord[] = [];
    const cursorReq = t.objectStore(STORE.outbox).openCursor();
    await new Promise<void>((resolve, reject) => {
      cursorReq.onsuccess = () => {
        const c = cursorReq.result;
        if (!c || rows.length >= limit) return resolve();
        rows.push(c.value as OutboxRecord);
        c.continue();
      };
      cursorReq.onerror = () => reject(cursorReq.error);
    });
    return rows;
  });
}

export interface FlushOutcome {
  sent: number;
  dropped: number;
  remaining: number;
  /** set when the queue could not be fully drained */
  error?: string;
}

let inFlight: Promise<FlushOutcome> | null = null;

/**
 * Send everything queued, oldest first.
 *
 * Ops are applied in order server-side, which matters: a set references the
 * session created just before it. A permanently rejected op (4xx) is moved to
 * the dead-letter store rather than dropped, so a bug leaves evidence instead
 * of a hole in the history.
 */
export function flush(): Promise<FlushOutcome> {
  if (inFlight) return inFlight;
  inFlight = run().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function run(): Promise<FlushOutcome> {
  let sent = 0;
  let dropped = 0;
  let error: string | undefined;

  for (;;) {
    const batch = await head(BATCH);
    if (!batch.length) break;

    let results: { id: string; ok: boolean; error?: string }[];
    try {
      const res = await api.post<{ results: typeof results }>('/api/sync', {
        ops: batch.map((r) => ({ id: r.id, op: r.op, payload: r.payload, at: r.at })),
      });
      results = res.results ?? [];
    } catch (err) {
      if (err instanceof OfflineError) error = 'offline';
      else if (err instanceof ApiError) error = err.status === 401 ? 'unauthorised' : err.message;
      else error = 'sync failed';
      break;
    }

    const byId = new Map(results.map((r) => [r.id, r]));
    let stalled = false;

    await tx([STORE.outbox, STORE.dead], 'readwrite', (t) => {
      const outbox = t.objectStore(STORE.outbox);
      const dead = t.objectStore(STORE.dead);

      for (const rec of batch) {
        const result = byId.get(rec.id);
        if (!result) {
          // the server stopped early; leave this one queued
          stalled = true;
          continue;
        }
        if (result.ok) {
          outbox.delete(rec.seq!);
          sent++;
          continue;
        }
        const permanent = /^4\d\d:/.test(result.error ?? '');
        if (permanent) {
          const { seq: _seq, ...rest } = rec;
          dead.add({ ...rest, lastError: result.error });
          outbox.delete(rec.seq!);
          dropped++;
        } else {
          outbox.put({ ...rec, tries: rec.tries + 1, lastError: result.error });
          stalled = true;
        }
      }
    });

    if (stalled) {
      error ??= 'server rejected part of the queue';
      break;
    }
  }

  return { sent, dropped, remaining: await depth(), error };
}

/** Ask the service worker to retry later, even if the app is closed by then. */
export async function requestBackgroundSync(): Promise<void> {
  try {
    const reg = await navigator.serviceWorker?.ready;
    // SyncManager is Chromium-only; Android Chrome is the target, and the
    // page-side triggers cover everything else.
    const sync = (reg as (ServiceWorkerRegistration & { sync?: { register(tag: string): Promise<void> } }) | undefined)
      ?.sync;
    await sync?.register('flush-outbox');
  } catch {
    // background sync unavailable or denied: the page will flush on next open
  }
}
